// DYOC Image Loader - Simplified and Reliable
// This module handles all image loading for the Design Your Own Cane page

const DYOCImageLoader = {
  // Direct product handle mappings for reliable image fetching
  productMappings: {
    wood: {
      'oak': { handle: 'wood-oak', fallback: '#D2691E' },
      'hickory': { handle: 'wood-hickory', fallback: '#8B4513' },
      'hickory-with-heartwood': { handle: 'wood-hickory-with-heartwood', fallback: '#A0522D' },
      'pure-hickory': { handle: 'wood-pure-hickory', fallback: '#654321' }
    },
    finish: {
      'gunstock-brown': { handle: 'stain-gunstock-brown', fallback: '#8B5A3C' },
      'medium-brown': { handle: 'stain-medium-brown', fallback: '#7B4225' },
      'dark-brown': { handle: 'stain-dark-brown', fallback: '#4B2F20' },
      'crimson': { handle: 'stain-crimson', fallback: '#DC143C' },
      'black-dye': { handle: 'stain-black-dye', fallback: '#1C1C1C' },
      'tung-oil': { handle: 'stain-tung-oil', fallback: '#CD853F' }
    },
    'horn-style': {
      'inside-cut': { handle: 'horn-style-inside-cut' },
      'round-horn': { handle: 'horn-style-round-horn' },
      'gentlemans': { handle: 'horn-style-gentlemans' },
      'birds-head': { handle: 'horn-style-birds-head' },
      'tactical': { handle: 'horn-style-tactical' },
      'elite': { handle: 'horn-style-elite' }
    },
    eyes: {
      'green-round': { handle: 'eyes-green-round' },
      'green-lifelike': { handle: 'eyes-green-lifelike' },
      'orange-lifelike': { handle: 'eyes-orange-lifelike' },
      'pink-lifelike': { handle: 'eyes-pink-lifelike' },
      'red-crystal': { handle: 'eyes-red-crystal' },
      'red-lifelike': { handle: 'eyes-red-lifelike' },
      'white-crystal': { handle: 'eyes-whitecrystal' },
      'yellow-crystal': { handle: 'eyes-yellowcrystal' },
      'yellow-lifelike': { handle: 'eyes-yellowlifelike' }
    },
    grips: {
      'top-grips': { handle: 'grips-top-grips' },
      'middle-grips': { handle: 'grips-middle-grips' },
      'bottom-grips': { handle: 'grips-bottom-grips' },
      'on-the-crook-1': { handle: 'grips-on-the-crook-1' },
      'top-grips-extended': { handle: 'grips-top-grips-extended' },
      'full-length-grips': { handle: 'grips-full-length-grips' },
      'full-length-grips-lateral': { handle: 'grips-full-length-grips-lateral' }
    },
    sharks: {
      '12': { handle: 'sharks-teeth-12' },
      'full-length': { handle: 'sharks-teeth-full-length' },
      '12-angled': { handle: 'sharks-teeth-12-angled-sharks-teeth' },
      '6-inside': { handle: 'sharks-teeth-6-inside-bottom-of-shaft' }
    },
    rumble: {
      '12-inch': { handle: 'rumble-strip-12' },
      'double-12': { handle: 'rumble-strip-double-rumble-strip-12' },
      '24-inch': { handle: 'rumble-strip-24' },
      'double-24': { handle: 'rumble-strip-double-rumble-strip-24' },
      'full-length': { handle: 'rumble-strip-full-length-of-shaft' }
    },
    strikingEdges: {
      'on-shaft': { handle: 'striking-edge-on-shaft' },
      'inside-bottom': { handle: 'striking-edge-inside-bottom-of-shaft' }
    },
  },

  // Cache for fetched product data
  productCache: {},
  
  // Image cache for quick access
  imageCache: {},

  // Initialize the loader
  init() {
    console.log('Initializing DYOC Image Loader...');
    
    // Start loading images for visible options
    this.loadVisibleImages();
    
    // Set up intersection observer for lazy loading
    this.setupLazyLoading();
    
    // Load from localStorage cache if available
    this.loadCachedImages();
    
    // Preload critical images
    this.preloadCriticalImages();
  },

  // Load images from localStorage cache
  loadCachedImages() {
    try {
      const cached = localStorage.getItem('dyoc_image_cache');
      if (cached) {
        const data = JSON.parse(cached);
        // Only use cache if less than 24 hours old
        if (data.timestamp && Date.now() - data.timestamp < 86400000) {
          this.imageCache = data.images || {};
          console.log('Loaded image cache from localStorage');
        }
      }
    } catch (e) {
      console.warn('Failed to load image cache:', e);
    }
  },

  // Save images to localStorage cache
  saveCacheImages() {
    try {
      localStorage.setItem('dyoc_image_cache', JSON.stringify({
        timestamp: Date.now(),
        images: this.imageCache
      }));
    } catch (e) {
      console.warn('Failed to save image cache:', e);
    }
  },

  // Fetch product data with retry logic
  async fetchProduct(handle, retries = 2) {
    // Check cache first
    if (this.productCache[handle]) {
      return this.productCache[handle];
    }

    for (let i = 0; i <= retries; i++) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) {
          throw new Error(`Product not found: ${handle}`);
        }
        
        const product = await response.json();
        this.productCache[handle] = product;
        return product;
      } catch (error) {
        console.warn(`Attempt ${i + 1} failed for ${handle}:`, error);
        if (i === retries) {
          return null;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
      }
    }
  },

  // Get image URL for an option
  async getImageUrl(category, optionId) {
    const cacheKey = `${category}_${optionId}`;
    
    // Check image cache first
    if (this.imageCache[cacheKey]) {
      return this.imageCache[cacheKey];
    }

    // Get product mapping
    const mapping = this.productMappings[category]?.[optionId];
    if (!mapping) {
      console.warn(`No mapping found for ${category}:${optionId}`);
      return null;
    }

    // Fetch product data
    const product = await this.fetchProduct(mapping.handle);
    if (!product) {
      return null;
    }

    // Get the featured image or first variant image
    let imageUrl = null;
    if (product.featured_image) {
      imageUrl = product.featured_image;
    } else if (product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    } else if (product.variants && product.variants[0] && product.variants[0].featured_image) {
      imageUrl = product.variants[0].featured_image.src;
    }

    if (imageUrl) {
      // Cache the image URL
      this.imageCache[cacheKey] = imageUrl;
      this.saveCacheImages();
    }

    return imageUrl;
  },

  // Load image for a specific option element
  async loadOptionImage(element) {
    const category = element.dataset.category;
    const optionId = element.dataset.optionId;
    
    if (!category || !optionId) {
      console.warn('Missing data attributes on element:', element);
      return;
    }

    // Check if already loaded
    if (element.dataset.imageLoaded === 'true') {
      return;
    }

    // Find the image element
    const imgContainer = element.querySelector('.wood-swatch, .finish-swatch, .horn-swatch, .crystal-swatch, .grip-swatch, .sharks-swatch, .rumble-swatch, .strikingEdges-swatch');
    if (!imgContainer) return;

    // Check if container already has a loaded image
    const existingImg = imgContainer.querySelector('img[src]:not([src=""])');
    if (existingImg && existingImg.complete) {
      element.dataset.imageLoaded = 'true';
      return;
    }

    // Add loading state
    imgContainer.classList.add('loading');

    try {
      const imageUrl = await this.getImageUrl(category, optionId);
      
      if (imageUrl) {
        // Create or update image element
        let img = imgContainer.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          imgContainer.appendChild(img);
        }

        // Set up image loading
        img.loading = 'lazy';
        img.decoding = 'async';
        
        // Use srcset for responsive images
        const sizes = [150, 300, 450];
        const srcset = sizes.map(size => {
          const url = imageUrl.replace(/\.(jpg|jpeg|png|webp)$/i, `_${size}x.$1`);
          return `${url} ${size}w`;
        }).join(', ');
        
        img.srcset = srcset;
        img.sizes = '(max-width: 768px) 80px, 150px';
        img.src = imageUrl.replace(/\.(jpg|jpeg|png|webp)$/i, '_150x.$1');
        img.alt = element.querySelector('label')?.textContent || optionId;
        
        // Handle load complete
        img.onload = () => {
          imgContainer.classList.remove('loading');
          imgContainer.classList.add('loaded');
          element.dataset.imageLoaded = 'true';
        };
        
        // Handle error with fallback
        img.onerror = () => {
          console.warn(`Failed to load image for ${category}:${optionId}`);
          imgContainer.classList.remove('loading');
          this.applyFallback(imgContainer, category, optionId);
        };
      } else {
        // No image found, use fallback
        this.applyFallback(imgContainer, category, optionId);
      }
    } catch (error) {
      console.error(`Error loading image for ${category}:${optionId}:`, error);
      imgContainer.classList.remove('loading');
      this.applyFallback(imgContainer, category, optionId);
    }
  },

  // Apply fallback color or placeholder
  applyFallback(container, category, optionId) {
    const mapping = this.productMappings[category]?.[optionId];
    const fallbackColor = mapping?.fallback;
    
    container.classList.add('fallback');
    
    if (fallbackColor) {
      // Apply fallback color
      container.style.backgroundColor = fallbackColor;
      container.innerHTML = '';
    } else {
      // Apply placeholder
      container.innerHTML = `<div class="placeholder">${optionId.replace(/-/g, ' ')}</div>`;
    }
  },

  // Load images for currently visible options
  loadVisibleImages() {
    const visibleSteps = document.querySelectorAll('.customizer-step:not([style*="display: none"])');
    
    visibleSteps.forEach(step => {
      const options = step.querySelectorAll('[data-category][data-option-id]');
      options.forEach(option => {
        this.loadOptionImage(option);
      });
    });
  },

  // Set up intersection observer for lazy loading
  setupLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: load all images
      this.loadAllImages();
      return;
    }

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadOptionImage(entry.target);
          imageObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '50px'
    });

    // Store observer globally for cleanup
    window.dyocImageObserver = imageObserver;

    // Observe all option elements
    document.querySelectorAll('[data-category][data-option-id]').forEach(element => {
      imageObserver.observe(element);
    });
  },

  // Preload critical images (first 3 of each category)
  async preloadCriticalImages() {
    const criticalImages = [];
    
    // Get first 3 options from each category
    ['wood', 'finish', 'horn-style'].forEach(category => {
      const options = Object.keys(this.productMappings[category]).slice(0, 3);
      options.forEach(optionId => {
        criticalImages.push(this.getImageUrl(category, optionId));
      });
    });

    // Preload images
    const imageUrls = await Promise.all(criticalImages);
    imageUrls.filter(url => url).forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url.replace(/\.(jpg|jpeg|png|webp)$/i, '_150x.$1');
      document.head.appendChild(link);
    });
  },

  // Load all images (fallback for no IntersectionObserver)
  loadAllImages() {
    document.querySelectorAll('[data-category][data-option-id]').forEach(element => {
      this.loadOptionImage(element);
    });
  },

};

// Initialize function
function initializeDYOCImages() {
  // Clear any existing observers
  if (window.dyocImageObserver) {
    window.dyocImageObserver.disconnect();
  }
  
  // Reset cache if URL has changed
  const currentUrl = window.location.href;
  if (window.lastDyocUrl && window.lastDyocUrl !== currentUrl) {
    console.log('URL changed, reinitializing DYOC images');
    DYOCImageLoader.imageCache = {};
  }
  window.lastDyocUrl = currentUrl;
  
  // Initialize the loader
  DYOCImageLoader.init();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDYOCImages);
} else {
  initializeDYOCImages();
}

// Listen for Shopify theme events (for AJAX navigation)
document.addEventListener('shopify:section:load', initializeDYOCImages);
document.addEventListener('shopify:section:reorder', initializeDYOCImages);

// Listen for variant changes
document.addEventListener('variant:changed', function() {
  setTimeout(initializeDYOCImages, 100);
});

// Listen for URL changes (for back/forward navigation)
let lastUrl = location.href;
new MutationObserver(() => {
  const url = location.href;
  if (url !== lastUrl) {
    lastUrl = url;
    setTimeout(initializeDYOCImages, 100);
  }
}).observe(document, { subtree: true, childList: true });

// Also reinitialize if the product form changes
document.addEventListener('product:loaded', initializeDYOCImages);

// Export for global access
window.DYOCImageLoader = DYOCImageLoader;
window.initializeDYOCImages = initializeDYOCImages;